import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as echarts from 'echarts/lib/echarts';
import 'echarts/lib/chart/bar';
import 'echarts/lib/component/legend';
import 'echarts/lib/component/legendScroll';
import 'echarts/lib/component/tooltip';
import 'echarts/lib/component/title';
import 'echarts/lib/component/dataZoom';
import 'echarts/lib/component/grid';
import 'echarts/lib/component/brush';
import 'echarts/lib/component/graphic';
import { throttle } from 'lodash';
import { useResizeObserver } from '../../hooks';


export const useChartInstance = ({ initOption = null }) => {
  const [instance, setInstance] = useState<echarts.ECharts | null>(null);

  // chart instance with init option
  const chartInstanceRefCb: any = useCallback((instance) => {
    setInstance(instance);
    if(initOption) { instance.setOption(initOption); }
  }, [initOption]);

  return { chartInstanceRefCb, instance };
}

// cleaned up Chart component
// uses ref to manage the chart
function BaseChart({
  chartInstanceRefCb,
  chartId = '',
  className = '',
  style = {},
}) {
  const el = useRef<HTMLDivElement>(null);
  const echartsInstanceRef = React.useRef<echarts.ECharts>();

  useEffect(() => {
    if (!el.current) return;

    echartsInstanceRef.current = echarts.init(el.current);
    chartInstanceRefCb(echartsInstanceRef.current);
  }, [chartInstanceRefCb]);

  const onResize = React.useMemo(
    () =>
      throttle(() => {
        const bounding = el.current?.getBoundingClientRect();

        // Check the bounding box is not empty, otherwise echarts will throw error
        if (!((bounding?.width || 0) * (bounding?.height || 0))) return;

        echartsInstanceRef.current?.resize({ animation: { duration: 200 } });
      }, 100),
    []
  );

  // Resize chart automatically on the container resize
  useResizeObserver({
    ref: el,
    onResize: () => requestAnimationFrame(onResize),
  });

  return <div ref={el} id={chartId} className={className} style={style} />;
}

export default React.memo(BaseChart);
